import { useRef, useCallback } from 'react';
import { getRecordedPath } from '../lib/navHistory';

/**
 * Persist a paginated list's loaded items, active filters and scroll position
 * across navigation into one of its own detail pages, so pressing "back"
 * restores the exact filtered view the user had (instead of resetting).
 *
 * The cache is restored ONLY when the user returns from a detail page of this
 * same list (detected via the recorded previous path). Arriving fresh from
 * anywhere else returns null — i.e. filters/pagination reset when the user
 * truly leaves the page.
 *
 * @param {string} key          unique cache key, e.g. 'companies'
 * @param {string} detailPrefix the list's detail-route prefix, e.g. '/dashboard/companies/'
 */
export default function useListStateCache(key, detailPrefix) {
  const storageKey = `listcache:${key}`;

  const read = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const write = useCallback((data) => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(data));
    } catch {
      /* quota / serialization issues are non-fatal */
    }
  }, [storageKey]);

  // Decide once, synchronously during the first render, whether to restore.
  const planRef = useRef(undefined);
  if (planRef.current === undefined) {
    const last = getRecordedPath();
    const cameFromDetail = !!(last && last.startsWith(detailPrefix));
    planRef.current = cameFromDetail ? read() : null;
  }

  return {
    restoredState: planRef.current, // null when not restoring
    write,
  };
}
